import { formErrorsFromFields, type FormErrors } from '@/lib/form-errors';
import type { TipoEncabezadoPlantilla } from '@/types/domain';
import type { EventoTemplateDef, PlantillaParametro } from '@/types/templates';
import { sortParametros, variablesDelCuerpo } from './templates-format';
import type { SendTestFormState, TemplateFormState } from './templates-payload';

/**
 * Validación del formulario en el cliente: espejo de `whatsapp-templates.schemas.ts` del backend.
 *
 * No pretende reemplazarlo (el backend valida lo mismo y más: el `unique` de evento + idioma, que el
 * mapeo use campos del catálogo, que el tenant tenga WhatsApp conectado…), sino no gastar un viaje al
 * servidor en lo evidente —nombre de Meta mal formado, cuerpo vacío, idioma inválido— y dejar el
 * mensaje debajo del campo.
 *
 * El caso propio de este módulo es el MAPEO: el backend exige que `parametros` cubra EXACTAMENTE las
 * variables `{{n}}` del cuerpo (`validarParametros`), así que se comprueba acá con el mismo criterio
 * (`variablesDelCuerpo`) para que el admin lo vea antes de guardar.
 */

/** Topes del contrato (`whatsapp-templates.schemas.ts`). */
export const TEMPLATE_LIMITS = {
  /** `nombreMeta`: `z.string().trim().min(1).max(512)` con el formato de Meta. */
  nombreMeta: 512,
  /** `idioma`: min 2, max 10, `es` o `es_CO`/`en_US`. */
  idioma: 10,
  /** `cuerpo`: min 1, max 1024. */
  cuerpo: 1024,
  /** `encabezadoTexto`: max 60 (solo con encabezado de TEXTO). */
  encabezadoTexto: 60,
  /** `pie`: max 60. */
  pie: 60,
  /** Posiciones de un `parametro`: 1..20. */
  posicionMax: 20,
  /** Cada valor de `valores` (preview y prueba): max 500. */
  valor: 500,
  /** `to` de la prueba: min 5, max 30. */
  destinatarioMin: 5,
  destinatarioMax: 30,
} as const;

/** Formato que Meta exige para el nombre de la plantilla. */
const NOMBRE_META_RE = /^[a-z0-9_]+$/;

/** `es` o `es_CO` / `en_US` (`normalizarIdioma` del backend los compara sin guiones). */
const IDIOMA_RE = /^[a-zA-Z]{2}([_-][a-zA-Z]{2})?$/;

/** «El nombre es obligatorio» / «nombreMeta: solo minúsculas…» / «…no puede pasar de 512…». */
export function validateNombreMeta(value: string): string | undefined {
  const text = value.trim();
  if (text.length === 0) return 'El nombre en Meta es obligatorio';
  if (text.length > TEMPLATE_LIMITS.nombreMeta) {
    return `El nombre no puede pasar de ${TEMPLATE_LIMITS.nombreMeta} caracteres (tienes ${text.length})`;
  }
  if (!NOMBRE_META_RE.test(text)) {
    return 'nombreMeta: solo minúsculas, números y guiones bajos (formato de Meta)';
  }
  return undefined;
}

/** Idioma: obligatorio, con la forma `es` o `es_CO`. */
export function validateIdioma(value: string): string | undefined {
  const text = value.trim();
  if (text.length === 0) return 'El idioma es obligatorio';
  if (text.length > TEMPLATE_LIMITS.idioma) {
    return `El idioma no puede pasar de ${TEMPLATE_LIMITS.idioma} caracteres (tienes ${text.length})`;
  }
  if (!IDIOMA_RE.test(text)) return 'idioma: formato `es` o `es_CO` / `en_US`';
  return undefined;
}

/**
 * Cuerpo: obligatorio (Meta no acepta una plantilla sin cuerpo), hasta 1024 caracteres y —regla de Meta
 * (subcode 2388299)— **las variables no pueden quedar al PRINCIPIO ni al FINAL**: «… con {{4}}.» se
 * rechaza igual que «… con {{4}}», porque después de la última variable solo hay puntuación.
 * Es el espejo del backend (`whatsapp-templates.service.ts::validarParaMeta`).
 */
export function validateCuerpo(value: string): string | undefined {
  const text = value.trim();
  if (text.length === 0) return 'El cuerpo es obligatorio';
  if (text.length > TEMPLATE_LIMITS.cuerpo) {
    return `El cuerpo no puede pasar de ${TEMPLATE_LIMITS.cuerpo} caracteres (tienes ${text.length})`;
  }
  if (/^\{\{\d+\}\}/.test(text)) {
    return 'El cuerpo no puede empezar con una variable (Meta lo rechaza): agregá un saludo o una palabra antes, ej. «Hola {{1}}, …»';
  }

  const cierre = text.lastIndexOf('}}');
  const cola = cierre >= 0 ? text.slice(cierre + 2).replace(/[\s.,;:!?¡¿"'()\-–—]/g, '') : '';
  if (/\{\{\d+\}\}/.test(text) && cola.length === 0) {
    return 'El cuerpo no puede terminar con una variable (Meta lo rechaza): después de la última variable escribí una palabra, ej. «… con {{4}}. ¡Gracias!»';
  }

  return undefined;
}

/** Encabezado de texto: obligatorio cuando el tipo es TEXTO y hasta 60 caracteres. */
export function validateEncabezadoTexto(
  value: string,
  encabezadoTipo: TipoEncabezadoPlantilla,
): string | undefined {
  if (encabezadoTipo !== 'TEXTO') return undefined;

  const text = value.trim();
  if (text.length === 0) return 'El encabezado de texto es obligatorio (o elige otro tipo)';
  if (text.length > TEMPLATE_LIMITS.encabezadoTexto) {
    return `El encabezado no puede pasar de ${TEMPLATE_LIMITS.encabezadoTexto} caracteres (tienes ${text.length})`;
  }
  return undefined;
}

/** Pie: opcional, hasta 60 caracteres. */
export function validatePie(value: string): string | undefined {
  const text = value.trim();
  if (text.length === 0) return undefined;
  if (text.length > TEMPLATE_LIMITS.pie) {
    return `El pie no puede pasar de ${TEMPLATE_LIMITS.pie} caracteres (tienes ${text.length})`;
  }
  return undefined;
}

/**
 * El mapeo tiene que cubrir EXACTAMENTE las variables del cuerpo (`validarParametros` del backend).
 *
 * Devuelve el primer problema encontrado, en el orden en que el admin lo puede arreglar: variables sin
 * mapear → mapeos que sobran → posición fuera de rango (1..20) → campo que no existe en el evento.
 */
export function validateParametros(
  parametros: PlantillaParametro[],
  cuerpo: string,
  def: EventoTemplateDef,
): string | undefined {
  const variables = variablesDelCuerpo(cuerpo);
  const ordenados = sortParametros(parametros);
  const posiciones = [...new Set(ordenados.map((parametro) => parametro.posicion))].sort(
    (a, b) => a - b,
  );

  const faltantes = variables.filter((variable) => !posiciones.includes(variable));
  if (faltantes.length > 0) {
    return `Falta mapear ${faltantes
      .map((variable) => `{{${variable}}}`)
      .join(', ')} del cuerpo a un campo de este evento`;
  }

  const sobrantes = posiciones.filter((posicion) => !variables.includes(posicion));
  if (sobrantes.length > 0) {
    return `El mapeo tiene ${sobrantes
      .map((posicion) => `{{${posicion}}}`)
      .join(', ')}, que ya no está en el cuerpo`;
  }

  const fueraDeRango = posiciones.filter(
    (posicion) => posicion < 1 || posicion > TEMPLATE_LIMITS.posicionMax,
  );
  if (fueraDeRango.length > 0) {
    return `Las variables van de 1 a ${TEMPLATE_LIMITS.posicionMax} (revisa ${fueraDeRango.join(', ')})`;
  }

  const claves = def.campos.map((campo) => campo.clave);
  const invalidos = [...new Set(ordenados.map((parametro) => parametro.campo))].filter(
    (campo) => !claves.includes(campo),
  );
  if (invalidos.length > 0) {
    return `Este evento no tiene esos campos: ${invalidos.join(', ')}. Disponibles: ${claves.join(', ')}`;
  }

  return undefined;
}

/**
 * Formulario de la plantilla → errores de formulario.
 *
 * El orden de los mensajes es el de los campos en pantalla y el mapeo cae en el campo `parametros` (que
 * es la sección donde se arregla, no en el aviso general).
 */
export function validateTemplateForm(
  form: TemplateFormState,
  def: EventoTemplateDef,
): FormErrors {
  return formErrorsFromFields({
    nombreMeta: validateNombreMeta(form.nombreMeta),
    idioma: validateIdioma(form.idioma),
    encabezadoTexto: validateEncabezadoTexto(form.encabezadoTexto, form.encabezadoTipo),
    cuerpo: validateCuerpo(form.cuerpo),
    pie: validatePie(form.pie),
    parametros: validateParametros(form.parametros, form.cuerpo, def),
  });
}

/** Valores por clave de un preview o de una prueba: cada uno hasta 500 caracteres (`valores` del schema). */
export function validateValores(valores: Record<string, string>): FormErrors {
  const fieldErrors: Record<string, string[]> = {};

  for (const [clave, valor] of Object.entries(valores)) {
    const text = valor.trim();
    if (text.length > TEMPLATE_LIMITS.valor) {
      fieldErrors[`valor:${clave}`] = [
        `El valor no puede pasar de ${TEMPLATE_LIMITS.valor} caracteres (tienes ${text.length})`,
      ];
    }
  }

  return { formMessages: [], fieldErrors };
}

/** Clave de campo del error de un valor en el diálogo de prueba/preview. */
export function valorFieldKey(clave: string): string {
  return `valor:${clave}`;
}

/** ¿Es una URL que el backend va a aceptar? (`z.string().url()` exige un esquema válido). */
export function isHttpUrl(value: string): boolean {
  try {
    const url = new URL(value.trim());
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * Formulario de la prueba → errores.
 *
 * `headerImageUrl` es obligatorio con encabezado de IMAGEN (el backend responde 400 si falta: Meta
 * rechaza la plantilla por parámetros faltantes) y el destinatario tiene el mismo mínimo y máximo que
 * el schema (`5..30`), porque el número va con código de país.
 */
export function validateSendTestForm(
  form: SendTestFormState,
  encabezadoTipo: TipoEncabezadoPlantilla,
): FormErrors {
  const base = formErrorsFromFields({
    to: validateDestinatario(form.to),
    headerImageUrl: validateHeaderImageUrl(form.headerImageUrl, encabezadoTipo),
  });
  const valores = validateValores(form.valores);

  return {
    formMessages: base.formMessages,
    fieldErrors: { ...base.fieldErrors, ...valores.fieldErrors },
  };
}

/** Destinatario: obligatorio y con el largo que valida el schema (5..30). */
export function validateDestinatario(value: string): string | undefined {
  const text = value.trim();
  if (text.length === 0) return 'El destinatario es obligatorio';
  if (text.length < TEMPLATE_LIMITS.destinatarioMin) {
    return 'Escribe el número con código de país (ej. 573001234567)';
  }
  if (text.length > TEMPLATE_LIMITS.destinatarioMax) {
    return `El destinatario no puede pasar de ${TEMPLATE_LIMITS.destinatarioMax} caracteres`;
  }
  return undefined;
}

/** URL pública de la imagen del encabezado (solo aplica a las plantillas de encabezado IMAGEN). */
export function validateHeaderImageUrl(
  value: string,
  encabezadoTipo: TipoEncabezadoPlantilla,
): string | undefined {
  const text = value.trim();

  if (encabezadoTipo !== 'IMAGEN') return undefined;
  if (text.length === 0) {
    return 'Esta plantilla tiene encabezado de IMAGEN: la URL de la imagen es obligatoria para enviarla';
  }
  if (!isHttpUrl(text)) return 'headerImageUrl debe ser una URL http(s) válida';

  return undefined;
}

